import { useState } from "react";
import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminUsers } from "@/hooks/use-resources";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Wind, AlertTriangle, CheckCircle2, Clock, User, Calendar, Home, UserCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type LaundryItem = { name: string; qty: number };
type LaundryJob = {
  id: number;
  status: string;
  cleaningJobId: number | null;
  apartmentName: string | null;
  apartmentId: number | null;
  assignedTo: number | null;
  assignedToName: string | null;
  takenItems: LaundryItem[] | null;
  returnedItems: LaundryItem[] | null;
  mismatch: boolean;
  mismatchDetails: { name: string; taken: number; returned: number }[] | null;
  notes: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  pending:     { label: "Pending",     variant: "secondary" },
  in_progress: { label: "In Progress", variant: "default" },
  completed:   { label: "Completed",   variant: "secondary" },
};

type FilterStatus = "all" | "pending" | "in_progress" | "completed";

function ItemList({ items, label }: { items: LaundryItem[] | null; label: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-0.5">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((item, i) => (
          <span key={i} className="text-xs bg-muted rounded px-1.5 py-0.5">{item.name} ×{item.qty}</span>
        ))}
      </div>
    </div>
  );
}

function JobCard({ job, laundryUsers, onAssign }: {
  job: LaundryJob;
  laundryUsers: { id: number; label: string }[];
  onAssign: (id: number, assignedTo: number | null) => void;
}) {
  const cfg = statusConfig[job.status] ?? { label: job.status, variant: "outline" as const };
  // Resolve the human name for assignedTo: prefer the joined name from the
  // server (survives if the user role changed); fall back to the live lookup.
  const claimedByLabel = job.assignedTo
    ? job.assignedToName
      ?? laundryUsers.find(u => u.id === job.assignedTo)?.label
      ?? `User #${job.assignedTo}`
    : null;

  return (
    <Card className={`border ${job.mismatch ? "border-destructive/60" : "border-border/50"}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={cfg.variant}>{cfg.label}</Badge>
            {(job.apartmentName ?? job.apartmentId) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Home className="w-3 h-3" />
                {job.apartmentName ?? `Unit #${job.apartmentId}`}
              </div>
            )}
            {claimedByLabel && job.status !== "completed" && (
              <Badge variant="outline" className="flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> {claimedByLabel}
              </Badge>
            )}
            {job.mismatch && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Mismatch
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {format(new Date(job.createdAt), "MMM d, yyyy")}
          </span>
        </div>

        {job.scheduledAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            {format(new Date(job.scheduledAt), "MMM d, yyyy · h:mm a")}
          </div>
        )}

        <div className="space-y-2">
          <ItemList items={job.takenItems} label="Taken to laundry" />
          <ItemList items={job.returnedItems} label="Returned" />
        </div>

        {job.mismatch && job.mismatchDetails && job.mismatchDetails.length > 0 && (
          <div className="bg-destructive/5 rounded p-2 space-y-0.5">
            <p className="text-xs font-medium text-destructive">Mismatch</p>
            {job.mismatchDetails.map((d, i) => (
              <p key={i} className="text-xs text-destructive">{d.name}: sent {d.taken}, returned {d.returned}</p>
            ))}
          </div>
        )}

        {job.notes && (
          <p className="text-xs text-muted-foreground border-t pt-2">{job.notes}</p>
        )}

        <div className="space-y-1 pt-1">
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <Select
              value={job.assignedTo ? String(job.assignedTo) : "unassigned"}
              onValueChange={v => onAssign(job.id, v === "unassigned" ? null : Number(v))}
            >
              <SelectTrigger className="h-8 text-xs flex-1">
                <SelectValue placeholder="Pre-assign (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned (shared queue)</SelectItem>
                {laundryUsers.map(u => (
                  <SelectItem key={u.id} value={String(u.id)}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[10px] text-muted-foreground pl-5">
            All laundry workers see every job. Pre-assign just suggests an owner; first worker to Start claims it.
          </p>
        </div>

        {job.completedAt && (
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Completed {format(new Date(job.completedAt), "MMM d, yyyy")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminLaundryJobs() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterStatus>("all");

  const { data: jobs = [], isLoading } = useQuery<LaundryJob[]>({
    queryKey: ["/api/admin/laundry-jobs"],
  });

  const { data: allUsers = [] } = useAdminUsers();

  const laundryUsers = (allUsers as any[])
    .filter((u: any) => u.role === "laundry" || (u.additionalRoles ?? []).includes("laundry"))
    .map((u: any) => ({ id: u.id, label: u.fullName || u.username }));

  const assignMutation = useMutation({
    mutationFn: async ({ id, assignedTo }: { id: number; assignedTo: number | null }) => {
      const res = await apiRequest("PUT", `/api/admin/laundry-jobs/${id}`, { assignedTo });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/laundry-jobs"] });
      toast({ title: "Job assigned" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const filtered = filter === "all" ? jobs : jobs.filter(j => j.status === filter);

  const counts = {
    pending:     jobs.filter(j => j.status === "pending").length,
    in_progress: jobs.filter(j => j.status === "in_progress").length,
    completed:   jobs.filter(j => j.status === "completed").length,
    mismatch:    jobs.filter(j => j.mismatch).length,
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Wind className="w-5 h-5 text-teal-600" />
            <h1 className="text-xl font-semibold">Laundry Jobs</h1>
            {counts.mismatch > 0 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {counts.mismatch} mismatch
              </Badge>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {([
            { key: "all",         label: `All (${jobs.length})` },
            { key: "pending",     label: `Pending (${counts.pending})` },
            { key: "in_progress", label: `In Progress (${counts.in_progress})` },
            { key: "completed",   label: `Completed (${counts.completed})` },
          ] as { key: FilterStatus; label: string }[]).map(f => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
            >
              {f.key === "in_progress" ? <Clock className="w-3.5 h-3.5 mr-1.5" /> : f.key === "completed" ? <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> : null}
              {f.label}
            </Button>
          ))}
        </div>

        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-40 w-full" />)}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <Wind className="w-10 h-10 opacity-30" />
            <p className="text-sm">No laundry jobs{filter !== "all" ? ` with status "${filter}"` : ""}.</p>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map(job => (
            <JobCard
              key={job.id}
              job={job}
              laundryUsers={laundryUsers}
              onAssign={(id, assignedTo) => assignMutation.mutate({ id, assignedTo })}
            />
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
